import React from 'react'
import type { Feedback, FfpDashboardData, Overview as OverviewData, PeriodKey, People } from '../../types'
import { CHANNEL_WORDS, clampPercent, FEEDBACK_CHANNELS, hasReadyComments, longMonth, periodRatingNote, plural, ratingSummary, shareOf, unratedText } from '../../utils'
import { Reveal } from '../Reveal'
import { SectionHeader } from '../SectionHeader'
import { SectionLink } from '../SectionLink'
import { Stars } from '../Stars'
import styles from './overview.module.sass'

type Props = {
  data: FfpDashboardData
  period: PeriodKey
}

// Three answers at a glance. The score card is OverviewQuery as it is (all
// onboarded people, no income filter): it never shares a sentence with the
// health cards' figures.
export const Overview = ({ data, period }: Props) => (
  <>
    <SectionHeader title="Financial Freedom Programme overview" />
    <div className={styles.grid}>
      <Reveal>
        <HelpingCard data={data} />
      </Reveal>
      <Reveal>
        <UsingCard people={data.people} />
      </Reveal>
      <Reveal>
        <OpinionCard feedback={data.feedback} period={period} />
      </Reveal>
    </div>
  </>
)

type OverviewFooterProps = {
  badge: string
  link: string
  to: string
  dark?: boolean
}

const OverviewFooter = ({ badge, link, to, dark = false }: OverviewFooterProps) => (
  <div className={dark ? `${styles.footer} ${styles.footerDark}` : styles.footer}>
    <span className={dark ? `${styles.badge} ${styles.badgeLime}` : styles.badge}>{badge}</span>
    <SectionLink id={to} className={dark ? `${styles.link} ${styles.linkLime}` : styles.link}>
      {link}
    </SectionLink>
  </div>
)

// No score to show in early days (the server sends none), or before the first
// weekly capture (it would read "0 out of 100").
const HelpingCard = ({ data }: { data: FfpDashboardData }) => {
  const { people, overview, first_month: firstMonth } = data
  const { score } = overview
  const waiting = people.early_days
    ? `First results appear once at least ${people.early_days_threshold} people have onboarded. ${people.onboarded} have so far.`
    : 'First results appear after the next weekly capture.'
  const body = people.early_days || !firstMonth || !score ? <NotEnoughYet sentence={waiting} /> : <Score score={score} overview={overview} />
  const badge = people.early_days ? `${people.onboarded} of ${people.early_days_threshold} people` : `All ${people.onboarded} onboarded people`

  return (
    <article className={`${styles.card} ${styles.dark}`}>
      <p className={`${styles.question} ${styles.questionLime}`}>Is this helping my people?</p>
      {body}
      <OverviewFooter badge={badge} link="See health" to="health" dark />
    </article>
  )
}

const NotEnoughYet = ({ sentence }: { sentence: string }) => (
  <>
    <p className={`${styles.big} ${styles.bigSmall} ${styles.onDark}`}>Not enough data yet</p>
    <p className={`${styles.sentence} ${styles.onDark}`}>{sentence}</p>
  </>
)

const scoreChange = (improvement: number): string => {
  const rounded = Math.round(improvement)
  if (rounded === 0) return 'Average financial health score, the same as when the programme started'
  return `Average financial health score, ${rounded > 0 ? 'up' : 'down'} ${Math.abs(rounded)}% since the programme started`
}

// Each share is labelled with its own month: the first one is the start of the
// 12-month window, which isn't first_month for a company with older captures.
const retirementLine = ({ first, latest }: OverviewData['retirement_likely_share']): string | null => {
  if (!latest) return null
  if (!first || first.month === latest.month || first.share === latest.share) return `${latest.share}% are likely to retire comfortably.`
  return `${latest.share}% are likely to retire comfortably, ${latest.share > first.share ? 'up' : 'down'} from ${first.share}% in ${longMonth(first.month)}.`
}

type ScoreProps = {
  score: NonNullable<OverviewData['score']>
  overview: OverviewData
}

const Score = ({ score, overview }: ScoreProps) => {
  const start = Math.round(score.start)
  const current = Math.round(score.current)
  const retirement = retirementLine(overview.retirement_likely_share)

  return (
    <>
      <div className={styles.score}>
        <div className={styles.scoreFigure}>
          <span className={styles.scoreValue}>{current}</span>
          <span className={styles.scoreOutOf}>out of 100</span>
        </div>
        <ScoreTrack start={start} current={current} startLabel="Start" />
      </div>
      <p className={styles.scoreChange}>{scoreChange(score.improvement_percentage)}</p>
      <Sentence text={retirement} dark />
    </>
  )
}

type TrackProps = {
  start: number
  current: number
  startLabel: string
}

// Where the score started and where it is now, on a 0-100 rail. The lower of
// the two is labelled to its left and the higher to its right.
const ScoreTrack = ({ start, current, startLabel }: TrackProps) => {
  const startAt = clampPercent(start)
  const nowAt = clampPercent(current)
  const low = Math.min(startAt, nowAt)
  const high = Math.max(startAt, nowAt)
  const rose = current >= start
  const from = `${startLabel} · ${start}`
  const now = `Now · ${current}`

  return (
    <div className={styles.track}>
      <span className={styles.rail} />
      <span className={styles.change} style={{ left: `${low}%`, width: `${high - low}%` }} />
      <span className={`${styles.dot} ${styles.dotStart}`} style={{ left: `${startAt}%` }} />
      <span className={`${styles.dot} ${styles.dotNow}`} style={{ left: `${nowAt}%` }} />
      <span className={`${styles.trackLabel} ${rose ? styles.toneStart : styles.toneNow}`} style={{ right: `${100 - low}%` }}>
        {rose ? from : now}
      </span>
      <span className={`${styles.trackLabel} ${rose ? styles.toneNow : styles.toneStart}`} style={{ left: `${high}%` }}>
        {rose ? now : from}
      </span>
      <span className={styles.trackMin}>0</span>
      <span className={styles.trackMax}>100</span>
    </div>
  )
}

const Sentence = ({ text, dark = false }: { text: string | null; dark?: boolean }) => {
  if (!text) return null

  return <p className={dark ? `${styles.sentence} ${styles.onDark}` : styles.sentence}>{text}</p>
}

const UsingCard = ({ people }: { people: People }) => {
  const { enrolled, onboarded, used_any_part: used } = people
  const share = shareOf(people.early_days ? onboarded : used, enrolled) ?? 0
  const sentence = people.early_days
    ? `${onboarded} of ${enrolled} enrolled people have onboarded.`
    : `${share}% of enrolled people have used at least one part of the programme, counting onboarding.`
  const badge = people.early_days ? 'Early days' : `${used} of ${enrolled} enrolled`

  return (
    <article className={styles.card}>
      <p className={styles.question}>Are they using it?</p>
      <p className={styles.big}>{share}%</p>
      <Sentence text={sentence} />
      <OverviewFooter badge={badge} link="See engagement" to="engagement" />
    </article>
  )
}

type Opinion = { big: string; rating: number | null; sentence: string; badge: string }

// The design's three states: no reviews, too few people to show, and ratings
// from the channels with enough people (utils.ratingSummary). A channel that
// doesn't qualify is left out of the headline and named.
const opinion = (feedback: Feedback, period: PeriodKey): Opinion => {
  const minimum = feedback.minimum_reviews
  const summary = ratingSummary(feedback)
  if (summary.state === 'none') {
    return { big: 'No ratings yet', rating: null, sentence: 'Ratings appear here once people rate their coaching sessions and workshops.', badge: 'Coaching and workshops' }
  }
  if (summary.state === 'hidden') {
    return {
      big: 'Hidden for now',
      rating: null,
      sentence: `${plural(summary.reviews, 'review')} so far. Ratings show once ${minimum} people have reviewed coaching, or ${minimum} have reviewed workshops, to protect anonymity.`,
      badge: `${plural(summary.reviews, 'review')} so far`,
    }
  }

  const { average, channels, reviews, top } = summary
  const only = channels.length === 1 ? channels[0] : null
  const rating = average.toFixed(1)
  const headline = `${rating} from ${reviews} ${only ? `${CHANNEL_WORDS[only].noun} reviews` : 'reviews'}`
  const note = periodRatingNote(feedback, period)

  if (note) return { big: `${rating} / 5`, rating: average, sentence: `${note}.`, badge: 'Since the start' }

  if (only) {
    const missing = FEEDBACK_CHANNELS.filter((channel) => channel !== only).map((channel) => unratedText(channel, feedback.channels[channel].reviews, minimum))
    return { big: `${rating} / 5`, rating: average, sentence: `${headline}. ${missing.join('. ')}.`, badge: `${CHANNEL_WORDS[only].name} only` }
  }

  return {
    big: `${rating} / 5`,
    rating: average,
    sentence: `${rating} / 5 from ${reviews} reviews of coaching and workshops.`,
    badge: `${CHANNEL_WORDS[top].name} rated highest at ${feedback.channels[top].average?.toFixed(1)}`,
  }
}

const OpinionCard = ({ feedback, period }: { feedback: Feedback; period: PeriodKey }) => {
  const { big, rating, sentence, badge } = opinion(feedback, period)
  const stars = rating === null ? null : <Stars rating={rating} size={24} />

  return (
    <article className={styles.card}>
      <p className={styles.question}>What do they think of it?</p>
      <div className={styles.rating}>
        <p className={rating === null ? `${styles.big} ${styles.bigSmall}` : styles.big}>{big}</p>
        {stars}
      </div>
      <Sentence text={sentence} />
      <OverviewFooter badge={badge} link={hasReadyComments(feedback) ? 'See what they say' : 'See ratings'} to="feedback" />
    </article>
  )
}
